import React, { type ReactElement, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  ButtonIcon,
  Icon,
  type IconKind,
  Menu,
  type MenuOption,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { SodFooter } from '../../../../shared/components';
import {
  ALLOWED_ACTIONS,
  CF_NEU_BLD,
  LIST_ACTIONS,
  TABS_FILTERS,
  VIOLATIONS_TAB_LIST_ACTIONS,
} from '../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../shared/hooks';
import { violationsTabActions } from '../../data-store/violationsTabSlice';

import './ViolationsTabFooter.css';

/**
 * ViolationsTabFooter
 */
function ViolationsTabFooter(): ReactElement {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const {
    controls: { tabInAction },
    bulkaction: { isFooterVisible, bulkCount, actionInfo, isBulkChecked },
    sidepanel: { isSidePanelVisible },
  } = useTypedSelector((state) => state.aag.violationsTab);

  const [optionsWithIcons, setOptionsWithIcons] = useState([] as MenuOption[]);

  // Constants
  const hasMitigateOption = useMemo(() => {
    if (tabInAction === null) {
      return false;
    }

    const originalOptions = VIOLATIONS_TAB_LIST_ACTIONS[tabInAction];
    const mitigateStatus = Boolean(
      originalOptions.find(
        (o) =>
          o.value === LIST_ACTIONS.MITIGATE.value ||
          o.value === LIST_ACTIONS.ADDMC.value
      )
    );

    return mitigateStatus;
  }, [tabInAction]);
  const isMitigatePossible = useMemo(() => {
    if (tabInAction === null) {
      return false;
    }

    if (isBulkChecked) {
      return true;
    }

    if (hasMitigateOption) {
      const uniqueRiskIds = [...new Set(actionInfo.map((item) => item.riskId))];

      if (uniqueRiskIds.length <= 1) {
        return true;
      }
    }

    return false;
  }, [actionInfo, tabInAction, isBulkChecked, hasMitigateOption]);
  const { rulesetName, riskName, riskId, addMcSodKey } = useMemo(() => {
    if (isMitigatePossible) {
      const uniqueRiskIds = [...new Set(actionInfo.map((item) => item.riskId))];

      if (uniqueRiskIds.length === 0 || isBulkChecked) {
        return {
          rulesetName: undefined,
          riskName: '',
          riskId: undefined,
          addMcSodKey: '',
        };
      }

      const targetRiskId = uniqueRiskIds[0];
      const targetRisk = actionInfo.find(
        (item) => item.riskId === targetRiskId
      );

      return {
        riskId: targetRisk?.riskId,
        rulesetName: targetRisk?.ruleset,
        riskName: targetRisk?.riskName,
        addMcSodKey: targetRisk?.id,
      };
    }

    return { rulesetName: undefined, riskName: '', riskId: undefined };
  }, [isMitigatePossible, actionInfo, isBulkChecked]);
  const actionMap = useMemo(() => {
    const actionsList: string[] = [];
    const intersectionMap =
      actionInfo.length > 0
        ? actionInfo
            .map((info) => info.allowedActions)
            .reduce((acc, curr) => acc.filter((val) => curr.includes(val)))
        : [];

    intersectionMap.forEach((action) => {
      if (action in ALLOWED_ACTIONS) {
        actionsList.push(
          ...ALLOWED_ACTIONS[action as keyof typeof ALLOWED_ACTIONS]
        );
      }
    });

    return actionsList;
  }, [actionInfo]);

  useEffect(() => {
    if (tabInAction === null) {
      return;
    }

    const originalOptions = VIOLATIONS_TAB_LIST_ACTIONS[tabInAction];

    const updatedOptions = originalOptions
      .filter((o) => {
        const isNotAllowed = Boolean(
          o.value === LIST_ACTIONS.REMOVEMC.value ||
            o.value === LIST_ACTIONS.REMEDIATE.value ||
            o.value === LIST_ACTIONS.GENERATEREPORT.value ||
            o.value === LIST_ACTIONS.VIEWDETAILS.value ||
            o.value === LIST_ACTIONS.MITIGATE.value ||
            o.value === LIST_ACTIONS.ADDMC.value ||
            o.isDivider
        );

        return !isNotAllowed;
      })
      .map((o) => ({ ...o, label: l(o.label) }));

    if (isBulkChecked) {
      setOptionsWithIcons(updatedOptions);

      return;
    }

    const mappedOptions = updatedOptions.map((o) => {
      const isDisabled = !actionMap.includes(o.value);

      return {
        ...o,
        isDisabled,
      };
    });

    setOptionsWithIcons(mappedOptions);
  }, [l, tabInAction, actionInfo, isBulkChecked, actionMap]);

  const handleOptionClick = (option: MenuOption) => {
    dispatch(
      violationsTabActions.setViolationsActionManager({
        isOpen: true,
        sodKey:
          option.value === LIST_ACTIONS.ADDMC.value &&
          addMcSodKey &&
          addMcSodKey.length > 0
            ? addMcSodKey
            : '',
        action: option.value,
        rulesetName,
        riskId,
        title: riskName,
        isBulkAction: true,
      })
    );
  };

  const openOptions = optionsWithIcons.slice(0, hasMitigateOption ? 1 : 2);
  const hiddenOptions = optionsWithIcons.slice(hasMitigateOption ? 1 : 2);
  const sodFooterClasses = cx(
    'Sod-ViolationsTabFooter',
    isFooterVisible && 'Sod-ViolationsTabFooter--isFooterVisible',
    isSidePanelVisible && 'Sod-ViolationsTabFooter--isSidePanelVisible'
  );

  const violationsText = `${l(
    '{count, plural, one {Violation} other {Violations}}',
    { count: bulkCount }
  )} (${bulkCount})`;
  const clearSelectionText = `${l('Clear Selection')} (${bulkCount})`;
  const selectedText = `${l('Selected')}:`;
  const mitigateButtonText = `${l(LIST_ACTIONS.MITIGATE.label)} (${bulkCount})`;
  const addMcButtonText = `${l(LIST_ACTIONS.ADDMC.label)} (${bulkCount})`;

  return (
    <SodFooter className={sodFooterClasses}>
      <Typography kind='body1' color={CF_NEU_BLD}>
        <span className='Sod-ViolationsTabFooter-statsub'>{selectedText}</span>
        {violationsText}
      </Typography>
      <Box className='Sod-ViolationsTabFooter-wrapper'>
        <Button
          kind='ghost'
          onClick={() => {
            dispatch(violationsTabActions.setBulkIsClear(true));
          }}>
          {clearSelectionText}
        </Button>
        <Box className='Sod-ViolationsTabFooter-actionBtnWrapper'>
          {openOptions.length > 0 &&
            openOptions.map((o) => {
              const buttonText = `${l(o.label)} (${bulkCount})`;

              return (
                <Button
                  key={o.value}
                  isDisabled={o.isDisabled}
                  kind='outlined'
                  leftIcon={<Icon kind={o.icon as IconKind} />}
                  onClick={() => handleOptionClick(o)}>
                  {buttonText}
                </Button>
              );
            })}
          {tabInAction === TABS_FILTERS.OPENCOUNT.value && (
            <Button
              kind='outlined'
              isDisabled={
                !isMitigatePossible ||
                (!isBulkChecked &&
                  !actionMap.includes(LIST_ACTIONS.MITIGATE.value))
              }
              leftIcon={<Icon kind={LIST_ACTIONS.MITIGATE.icon as IconKind} />}
              rightIcon={
                !isMitigatePossible ? (
                  <Tooltip
                    trigger={
                      <Icon
                        kind='AlertInfoFilled'
                        color='--color-foreground-info-subtle'
                      />
                    }
                    text={l(
                      'Mitigation is only available when all selected violations are of the same risk'
                    )}
                    placement='top-end'
                    isPortalEnabled
                  />
                ) : (
                  <div />
                )
              }
              onClick={() => handleOptionClick(LIST_ACTIONS.MITIGATE)}>
              {mitigateButtonText}
            </Button>
          )}
          {tabInAction === TABS_FILTERS.MITIGATEDCOUNT.value && (
            <Button
              kind='outlined'
              isDisabled={
                !isMitigatePossible ||
                (!isBulkChecked &&
                  !actionMap.includes(LIST_ACTIONS.ADDMC.value))
              }
              leftIcon={<Icon kind={LIST_ACTIONS.ADDMC.icon as IconKind} />}
              rightIcon={
                !isMitigatePossible ? (
                  <Tooltip
                    trigger={
                      <Icon
                        kind='AlertInfoFilled'
                        color='--color-foreground-info-subtle'
                      />
                    }
                    text={l(
                      'Add mitigation controls is only available when all selected violations are of the same risk'
                    )}
                    placement='top-end'
                    isPortalEnabled
                  />
                ) : (
                  <div />
                )
              }
              onClick={() => handleOptionClick(LIST_ACTIONS.ADDMC)}>
              {addMcButtonText}
            </Button>
          )}
          {hiddenOptions.length > 0 && (
            <Menu
              placement='bottom-end'
              options={hiddenOptions}
              onChange={handleOptionClick}
              minOptionsForSearch={hiddenOptions.length + 1}
              trigger={
                <ButtonIcon
                  size='medium'
                  kind='ghost'
                  ariaLabel={l('more')}
                  icon={<Icon kind='MoreVertical' />}
                />
              }
              isPortalEnabled
            />
          )}
        </Box>
      </Box>
    </SodFooter>
  );
}

export default ViolationsTabFooter;
